import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { ComponentProps, ForwardedRef } from 'react';
import { forwardRef, useId } from 'react';

export const Select = forwardRef(function CustomSelect(
  {
    error,
    label,
    placeHolder,
    children,
    value,
    disabled,
    readOnly,
    menuVariant = 'default',
    ...props
  }: Omit<ComponentProps<typeof MuiSelect>, 'error' | 'labelId'> & {
    error?: string | undefined;
    placeHolder?: string;
    menuVariant?: 'default' | 'grey';
  },
  ref: ForwardedRef<HTMLDivElement>,
) {
  const errorId = useId();
  const labelId = useId();

  return (
    <FormControl
      className={fullWidth}
      error={!!error}
      aria-readonly={readOnly || disabled}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <MuiSelect
        {...props}
        aria-describedby={errorId}
        aria-readonly={readOnly || disabled}
        label={label}
        labelId={labelId}
        ref={ref}
        defaultValue=""
        readOnly={readOnly || disabled}
        value={value ?? (placeHolder ? 'placeholder' : '')}
        inputProps={{
          ...(readOnly || disabled
            ? {
                tabIndex: -1,
                readOnly: true,
                'aria-readonly': true,
              }
            : {}),
          ...(menuVariant === 'grey' ? greyVariantProps : {}),
        }}
      >
        {placeHolder && (
          <MenuItem
            style={{ display: 'none' }}
            key="placeholder"
            disabled
            value="placeholder"
          >
            {placeHolder}
          </MenuItem>
        )}
        {children}
      </MuiSelect>
      <FormHelperText id={errorId}>{error}</FormHelperText>
    </FormControl>
  );
});

const greyVariantProps = {
  sx: { display: 'flex', alignItems: 'center' },
  MenuProps: {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.surface.surfaceContainerHigh,
      },
    },
  },
};

export function MenuItem({
  hoverVariant = 'default',
  ...props
}: ComponentProps<typeof MuiMenuItem> & {
  hoverVariant?: 'default' | 'purple';
}) {
  return (
    <MuiMenuItem
      className={hoverVariant === 'purple' ? hoverItemPurpleStyle : undefined}
      {...props}
    />
  );
}

const fullWidth = css({
  flex: 1,
  height: 'fit-content',
});

const hoverItemPurpleStyle = css((theme) => ({
  ':hover': {
    backgroundColor: theme.palette.secondary.secondaryContainer,
  },
  ':focus': {
    backgroundColor: theme.palette.secondary.secondaryContainer,
  },
}));
